/**
 * PSV-1727 — bottom-left per-stat upgrade HUD panel + energy bar.
 *
 * Starblast-style: eight stats (shared/upgradeStats STAT_DEFS), each
 * levelled 0..MAX_STAT_LEVEL with gems, one row per stat with hotkey
 * badge, short label, level pips, and next-cost readout. Above the rows
 * sits the header + energy bar (fire cost / regen readout, fed at
 * broadcast rate).
 *
 * Hotkeys 1–8 are handled by ArenaController's keyboard bindings — the
 * panel only RENDERS state and provides the click path (row click →
 * `onUpgrade(statId)`, gated on affordability). The server is
 * authoritative: `StatUpgraded` / `StatUpgradeRejected` broadcasts drive
 * the actual state, and the panel is optimistic-free — the caller pushes
 * confirmed state back in via `setGems` / `setLevels` / `setEnergy`.
 *
 * Interaction uses the container-level `setInteractive` pattern from
 * UpgradePanel (PSV-1338 Bug 1 Pass 3): one hit rect over the whole
 * panel, pointerdown maps container-local Y to a row index, and
 * `stopPropagation()` always fires so panel clicks never discharge the
 * ship's weapon. pointermove/pointerout on the same hit rect drive the
 * hover highlight for affordable rows (repainted only when the hovered
 * row index changes).
 */

import Phaser from 'phaser';

import {
  MAX_STAT_LEVEL,
  STAT_DEFS,
  STAT_ORDER,
  emptyStatLevels,
  nextStatCost,
  type StatId,
  type StatLevels,
} from '../../../server/shared/upgradeStats';

// PSV-1902 UX — wider panel fits full stat words; the extra bottom
// margin (46 vs 16) keeps the panel clear of the controls bar.
const PANEL_WIDTH = 300;
const PANEL_MARGIN = 16;
const PANEL_BOTTOM_MARGIN = 46;
const PAD_X = 12;

// Header (container-local coords).
const TITLE_Y = 8;

// Energy section (container-local coords).
const ENERGY_LABEL_Y = 26;
const ENERGY_BAR_X = PAD_X;
const ENERGY_BAR_Y = 42;
const ENERGY_BAR_WIDTH = PANEL_WIDTH - PAD_X * 2; // 232
const ENERGY_BAR_HEIGHT = 10;

// Thin accent divider between the energy section and the stat rows.
const DIVIDER_Y = ENERGY_BAR_Y + ENERGY_BAR_HEIGHT + 8; // 60

// Stat rows — pointerdown maps localY into this band to pick a row, so
// keep in sync with the visual paint in refresh().
const ROWS_TOP = 66;
const ROW_HEIGHT = 24;
const ROW_COUNT = STAT_DEFS.length;

const PANEL_HEIGHT = ROWS_TOP + ROW_COUNT * ROW_HEIGHT + 10; // 268
const PANEL_RADIUS = 8;

// Per-row geometry (container-local X offsets).
const BADGE_X = PAD_X;
const BADGE_SIZE = 16;
const BADGE_RADIUS = 4;
const LABEL_X = 36;
const PIPS_X = 118;
const PIP_SIZE = 7;
const PIP_GAP = 1;
const PIP_RADIUS = 2;
const COST_RIGHT_X = PANEL_WIDTH - PAD_X;

const PANEL_BG_COLOR = 0x0b101c;
const PANEL_BG_ALPHA = 0.92;
const PANEL_BORDER_COLOR = 0x2c3a5c;
const ACCENT_LINE_COLOR = 0x35c4f0;

const ENERGY_BG_COLOR = 0x1a2233;
// Fill shifts cyan → amber → red as the bar drains; each fill color has
// a lighter partner for the 2px glossy top strip.
const ENERGY_FILL_HIGH = 0x35c4f0;
const ENERGY_FILL_MID = 0xffb347;
const ENERGY_FILL_LOW = 0xff5577;
const ENERGY_GLOSS_HIGH = 0x8fe3ff;
const ENERGY_GLOSS_MID = 0xffd9a0;
const ENERGY_GLOSS_LOW = 0xff9db2;

const PIP_FILLED_COLOR = 0x44ff7a;
const PIP_EMPTY_COLOR = 0x232c42;
const PIP_MAX_COLOR = 0xffd84a;

const BADGE_BG_COLOR = 0x141b2c;
const BADGE_BORDER_COLOR = 0x3a4768;
const AFFORD_COLOR = 0x44ff7a;
const AFFORD_UNDERLAY_ALPHA = 0.06;
const AFFORD_UNDERLAY_HOVER_ALPHA = 0.13;

const COLOR_TITLE = '#7fd6ea';
const COLOR_TEXT = '#e8ecf5';
const COLOR_TEXT_DIM = '#8a93ad';
const COLOR_AFFORD = '#44ff7a';
const COLOR_GOLD = '#ffd84a';

export type StatUpgradeHandler = (statId: StatId) => void;

/** Mutable per-row render refs — built once, repainted in refresh(). */
interface RowRefs {
  hotkeyText: Phaser.GameObjects.Text;
  costText: Phaser.GameObjects.Text;
  /** Mirror of refresh()'s affordability, checked in pointerdown/hover. */
  affordable: boolean;
}

export class StatPanel {
  private readonly scene: Phaser.Scene;
  private readonly onUpgrade: StatUpgradeHandler;

  private container: Phaser.GameObjects.Container | null = null;
  private energyBar: Phaser.GameObjects.Graphics | null = null;
  private energyText: Phaser.GameObjects.Text | null = null;
  /** One shared Graphics for all rows' underlays + hotkey badges. */
  private rowFx: Phaser.GameObjects.Graphics | null = null;
  /** One shared Graphics for all rows' pips — cleared + repainted per refresh. */
  private pips: Phaser.GameObjects.Graphics | null = null;
  private rows: RowRefs[] = [];

  private currentGems = 0;
  private levels: StatLevels = emptyStatLevels();
  // Displayed (rounded) energy values — setEnergy() runs at broadcast
  // rate (~10Hz), so we skip the repaint when the rounded pair is stable.
  private shownEnergy = 0;
  private shownEnergyCap = 0;
  /** Row index under the pointer (hover highlight), or null. */
  private hoveredRow: number | null = null;
  private tooltip: Phaser.GameObjects.Container | null = null;
  /** PSV-1906 balance — stat costs scale with the ship tier. */
  private classTier = 1;
  private destroyed = false;

  constructor(scene: Phaser.Scene, onUpgrade: StatUpgradeHandler) {
    this.scene = scene;
    this.onUpgrade = onUpgrade;
    this.build();
    this.refresh();
    this.repaintEnergy();
  }

  /** PSV-1906 balance — stat prices scale with the ship tier. */
  setClassTier(tier: number): void {
    if (this.classTier === tier) return;
    this.classTier = tier;
    this.refresh();
  }

  setGems(n: number): void {
    if (this.currentGems === n) return;
    this.currentGems = n;
    this.refresh();
  }

  setLevels(levels: StatLevels): void {
    // Defensive copy — the caller may mutate its own object between calls.
    this.levels = { ...levels };
    this.refresh();
  }

  /**
   * Update only the energy bar. Called at broadcast rate (~10Hz), so it
   * must stay cheap: one Graphics repaint + one Text, skipped entirely
   * when the rounded values haven't moved.
   */
  setEnergy(current: number, cap: number): void {
    if (this.destroyed) return;
    const cur = Math.round(current);
    const capR = Math.round(cap);
    if (cur === this.shownEnergy && capR === this.shownEnergyCap) return;
    this.shownEnergy = cur;
    this.shownEnergyCap = capR;
    this.repaintEnergy();
  }

  /**
   * Returns true if the given pointer screen position falls inside the
   * panel's bounding rect. Used by ArenaController.handleFireInput to
   * suppress the fire shot when the user clicks a stat row (otherwise
   * the same pointerdown that the row consumed ALSO reaches the
   * scene-level fire handler).
   *
   * Panel uses `setScrollFactor(0)` → container x/y are in screen
   * coordinates, so we compare against `pointer.x` / `pointer.y` directly
   * (NOT worldX/worldY).
   */
  containsPointer(pointer: { x: number; y: number }): boolean {
    if (this.destroyed || this.container === null) return false;
    const px = pointer.x;
    const py = pointer.y;
    const x0 = this.container.x;
    const y0 = this.container.y;
    return (
      px >= x0 &&
      px <= x0 + PANEL_WIDTH &&
      py >= y0 &&
      py <= y0 + PANEL_HEIGHT
    );
  }

  destroy(): void {
    this.tooltip?.destroy(true);
    this.tooltip = null;
    if (this.destroyed) return;
    this.destroyed = true;
    this.container?.destroy(true);
    this.container = null;
    this.energyBar = null;
    this.energyText = null;
    this.rowFx = null;
    this.pips = null;
    this.rows = [];
  }

  // ---------- private ----------

  private build(): void {
    const cam = this.scene.cameras.main;
    const panelX = PANEL_MARGIN;
    const panelY = cam.height - PANEL_HEIGHT - PANEL_BOTTOM_MARGIN;

    this.container = this.scene.add
      .container(panelX, panelY)
      .setScrollFactor(0)
      .setDepth(18);

    // Background (drawn at container-local origin) — rounded card with a
    // subtle cyan-tinted border and a thin accent divider under the
    // energy section.
    const background = this.scene.add.graphics();
    background.fillStyle(PANEL_BG_COLOR, PANEL_BG_ALPHA);
    background.fillRoundedRect(0, 0, PANEL_WIDTH, PANEL_HEIGHT, PANEL_RADIUS);
    background.lineStyle(1, PANEL_BORDER_COLOR, 1);
    background.strokeRoundedRect(0, 0, PANEL_WIDTH, PANEL_HEIGHT, PANEL_RADIUS);
    background.lineStyle(1, ACCENT_LINE_COLOR, 0.35);
    background.lineBetween(PAD_X, DIVIDER_Y, PANEL_WIDTH - PAD_X, DIVIDER_Y);
    this.container.add(background);

    // Header — spaced small-caps title, dim cyan.
    const title = this.scene.add.text(PAD_X, TITLE_Y, 'U P G R A D E S', {
      fontSize: '11px',
      color: COLOR_TITLE,
    });
    this.container.add(title);

    // Energy section — static label, mutable bar + numeric readout.
    const energyLabel = this.scene.add.text(PAD_X, ENERGY_LABEL_Y, 'ENERGY', {
      fontSize: '10px',
      color: COLOR_TEXT_DIM,
    });
    this.container.add(energyLabel);

    this.energyText = this.scene.add
      .text(ENERGY_BAR_X + ENERGY_BAR_WIDTH, ENERGY_LABEL_Y, '', {
        fontSize: '10px',
        color: COLOR_TEXT,
      })
      .setOrigin(1, 0);
    this.container.add(this.energyText);

    this.energyBar = this.scene.add.graphics();
    this.container.add(this.energyBar);

    // Stat rows — static label/badge-digit texts once, mutable cost
    // texts, one shared Graphics for underlays + badges (below the
    // texts) and one for every row's pips.
    this.rowFx = this.scene.add.graphics();
    this.container.add(this.rowFx);

    this.pips = this.scene.add.graphics();
    this.container.add(this.pips);

    this.rows = [];
    for (let i = 0; i < ROW_COUNT; i++) {
      const def = STAT_DEFS[i]!;
      const rowY = ROWS_TOP + i * ROW_HEIGHT;

      // Hotkey digit sits centered inside the rounded badge painted by
      // paintRowFx() — digit only, no brackets.
      const hotkeyText = this.scene.add
        .text(BADGE_X + BADGE_SIZE / 2, rowY + ROW_HEIGHT / 2, String(def.hotkey), {
          fontSize: '10px',
          color: COLOR_TEXT_DIM,
        })
        .setOrigin(0.5, 0.5);
      this.container.add(hotkeyText);

      const label = this.scene.add.text(LABEL_X, rowY + 5, def.label, {
        fontSize: '12px',
        color: COLOR_TEXT,
      });
      this.container.add(label);

      const costText = this.scene.add
        .text(COST_RIGHT_X, rowY + 6, '', {
          fontSize: '11px',
          color: COLOR_TEXT_DIM,
        })
        .setOrigin(1, 0);
      this.container.add(costText);

      this.rows.push({ hotkeyText, costText, affordable: false });
    }

    // Container-level interactive over the full panel rect — same pattern
    // as UpgradePanel (PSV-1338 Bug 1 Pass 3): scrollFactor=0 doesn't
    // propagate to children, so per-row hit areas would be computed in
    // the wrong space. Phaser hands the pointer handlers localX/localY
    // already transformed through the container's matrix, so the row pick
    // is straight band math on localY.
    this.container.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, PANEL_WIDTH, PANEL_HEIGHT),
      Phaser.Geom.Rectangle.Contains,
    );
    this.container.input!.cursor = 'pointer';
    this.container.on(
      'pointerdown',
      (
        _pointer: Phaser.Input.Pointer,
        _localX: number,
        localY: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        // ALWAYS stop propagation — a click anywhere on the panel must
        // never discharge the ship's weapon (same rationale as
        // UpgradePanel Bug 1).
        event.stopPropagation();
        const rowIndex = this.rowIndexAt(localY);
        if (rowIndex === null) return;
        const row = this.rows[rowIndex];
        if (row === undefined || !row.affordable) return;
        this.onUpgrade(STAT_ORDER[rowIndex]!);
      },
      this,
    );

    // Hover highlight — only the underlay/badge Graphics repaints, and
    // only when the hovered row index actually changes.
    this.container.on(
      'pointermove',
      (_pointer: Phaser.Input.Pointer, _localX: number, localY: number) => {
        this.setHoveredRow(this.rowIndexAt(localY));
      },
      this,
    );
    this.container.on(
      'pointerout',
      () => {
        this.setHoveredRow(null);
      },
      this,
    );
  }

  /** Map container-local Y to a stat row index, or null outside the band. */
  private rowIndexAt(localY: number): number | null {
    if (localY < ROWS_TOP) return null; // header/energy section
    const rowIndex = Math.floor((localY - ROWS_TOP) / ROW_HEIGHT);
    return rowIndex >= 0 && rowIndex < ROW_COUNT ? rowIndex : null;
  }

  private setHoveredRow(rowIndex: number | null): void {
    if (this.hoveredRow === rowIndex) return;
    this.hoveredRow = rowIndex;
    this.paintRowFx();
    this.renderTooltip();
  }

  /**
   * PSV-1902 UX — hover tooltip: full stat name, what it does, and the
   * per-level effect. Sits to the RIGHT of the hovered row (never covers
   * the panel or the controls bar).
   */
  private renderTooltip(): void {
    this.tooltip?.destroy(true);
    this.tooltip = null;
    if (this.hoveredRow === null || this.container === null) return;
    const def = STAT_DEFS[this.hoveredRow];
    if (def === undefined) return;
    const rowY = ROWS_TOP + this.hoveredRow * ROW_HEIGHT;

    const tooltip = this.scene.add.container(PANEL_WIDTH + 8, rowY - 6);
    const text = this.scene.add.text(8, 6, `${def.name}\n${def.desc}\n${def.effect}`, {
      fontSize: '11px',
      color: '#dfe9f5',
      lineSpacing: 3,
    });
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x0b101c, 0.95);
    bg.fillRoundedRect(0, 0, text.width + 16, text.height + 12, 6);
    bg.lineStyle(1, 0x35c4f0, 0.7);
    bg.strokeRoundedRect(0, 0, text.width + 16, text.height + 12, 6);
    tooltip.add([bg, text]);
    this.container.add(tooltip);
    this.tooltip = tooltip;
  }

  /** Repaint pips + underlays/badges + cost/hotkey texts from gems/levels. */
  private refresh(): void {
    if (this.destroyed || this.pips === null) return;

    this.pips.clear();
    for (let i = 0; i < ROW_COUNT; i++) {
      const def = STAT_DEFS[i]!;
      const row = this.rows[i]!;
      const level = this.levels[def.id];
      const rowY = ROWS_TOP + i * ROW_HEIGHT;
      const pipY = rowY + Math.floor((ROW_HEIGHT - PIP_SIZE) / 2);

      const atMax = level >= MAX_STAT_LEVEL;
      const cost = nextStatCost(level, this.classTier);
      const affordable = cost !== null && this.currentGems >= cost;
      row.affordable = affordable;

      // Pips: earned levels filled green (gold when maxed), rest dark.
      const filledColor = atMax ? PIP_MAX_COLOR : PIP_FILLED_COLOR;
      for (let p = 0; p < MAX_STAT_LEVEL; p++) {
        const pipX = PIPS_X + p * (PIP_SIZE + PIP_GAP);
        this.pips.fillStyle(p < level ? filledColor : PIP_EMPTY_COLOR, 1);
        this.pips.fillRoundedRect(pipX, pipY, PIP_SIZE, PIP_SIZE, PIP_RADIUS);
      }

      // Cost: gold gem glyph + amount when buyable, dim otherwise;
      // maxed stats show a gold MAX.
      row.costText.setText(cost === null ? 'MAX' : `◆ ${cost}`);
      row.costText.setColor(
        cost === null ? COLOR_GOLD : affordable ? COLOR_GOLD : COLOR_TEXT_DIM,
      );
      // Hotkey digit brightens green when the upgrade is buyable —
      // mirrors the key 1–8 path in ArenaController being a live action.
      row.hotkeyText.setColor(affordable ? COLOR_AFFORD : COLOR_TEXT_DIM);
    }

    this.paintRowFx();
  }

  /**
   * Repaint the shared row-chrome Graphics: faint green underlay on
   * affordable rows (stronger when hovered) + the rounded hotkey badge
   * per row (border brightens green when affordable).
   */
  private paintRowFx(): void {
    if (this.destroyed || this.rowFx === null) return;

    this.rowFx.clear();
    for (let i = 0; i < ROW_COUNT; i++) {
      const row = this.rows[i]!;
      const rowY = ROWS_TOP + i * ROW_HEIGHT;

      if (row.affordable) {
        const alpha =
          i === this.hoveredRow ? AFFORD_UNDERLAY_HOVER_ALPHA : AFFORD_UNDERLAY_ALPHA;
        this.rowFx.fillStyle(AFFORD_COLOR, alpha);
        this.rowFx.fillRoundedRect(6, rowY + 1, PANEL_WIDTH - 12, ROW_HEIGHT - 2, 5);
      }

      const badgeY = rowY + (ROW_HEIGHT - BADGE_SIZE) / 2;
      this.rowFx.fillStyle(BADGE_BG_COLOR, 1);
      this.rowFx.fillRoundedRect(BADGE_X, badgeY, BADGE_SIZE, BADGE_SIZE, BADGE_RADIUS);
      this.rowFx.lineStyle(1, row.affordable ? AFFORD_COLOR : BADGE_BORDER_COLOR, 0.9);
      this.rowFx.strokeRoundedRect(BADGE_X, badgeY, BADGE_SIZE, BADGE_SIZE, BADGE_RADIUS);
    }
  }

  /** Repaint only the energy bar + numeric readout (setEnergy fast path). */
  private repaintEnergy(): void {
    if (this.destroyed || this.energyBar === null) return;
    const cap = Math.max(1, this.shownEnergyCap);
    const fraction = Math.max(0, Math.min(1, this.shownEnergy / cap));

    // Rounded-capsule track + fill; fill shifts cyan → amber → red as
    // the bar drains (cheap: pick color by fraction).
    const fillColor =
      fraction <= 0.15 ? ENERGY_FILL_LOW : fraction <= 0.3 ? ENERGY_FILL_MID : ENERGY_FILL_HIGH;
    const glossColor =
      fraction <= 0.15 ? ENERGY_GLOSS_LOW : fraction <= 0.3 ? ENERGY_GLOSS_MID : ENERGY_GLOSS_HIGH;
    const capsuleRadius = ENERGY_BAR_HEIGHT / 2;

    this.energyBar.clear();
    this.energyBar.fillStyle(ENERGY_BG_COLOR, 1);
    this.energyBar.fillRoundedRect(
      ENERGY_BAR_X,
      ENERGY_BAR_Y,
      ENERGY_BAR_WIDTH,
      ENERGY_BAR_HEIGHT,
      capsuleRadius,
    );
    const fillWidth = Math.round(ENERGY_BAR_WIDTH * fraction);
    if (fillWidth > 0) {
      // Clamp the corner radius so a near-empty bar still renders as a
      // clean sliver instead of a broken rounded-rect.
      const fillRadius = Math.min(capsuleRadius, fillWidth / 2);
      this.energyBar.fillStyle(fillColor, 1);
      this.energyBar.fillRoundedRect(
        ENERGY_BAR_X,
        ENERGY_BAR_Y,
        fillWidth,
        ENERGY_BAR_HEIGHT,
        fillRadius,
      );
      // 2px glossy top strip, inset past the rounded ends.
      const glossWidth = fillWidth - capsuleRadius * 2;
      if (glossWidth > 2) {
        this.energyBar.fillStyle(glossColor, 0.35);
        this.energyBar.fillRect(
          ENERGY_BAR_X + capsuleRadius,
          ENERGY_BAR_Y + 2,
          glossWidth,
          2,
        );
      }
    }
    this.energyBar.lineStyle(1, PANEL_BORDER_COLOR, 1);
    this.energyBar.strokeRoundedRect(
      ENERGY_BAR_X,
      ENERGY_BAR_Y,
      ENERGY_BAR_WIDTH,
      ENERGY_BAR_HEIGHT,
      capsuleRadius,
    );

    this.energyText?.setText(`${this.shownEnergy}/${this.shownEnergyCap}`);
  }
}
